import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, from, of, Subject, timer } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  pluck,
  switchMap,
  tap,
} from 'rxjs/operators';
import { TypedFormBuilder } from 'ZalamoLibrary/src/lib/ui/form/TypedFormBuilder';
import { HttpClient } from '@angular/common/http';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-wallart-multi',
    templateUrl: './wallart-multi.component.html',
    styleUrls: ['./wallart-multi.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WallartMultiComponent extends AliveState implements OnInit {
  scenes = [
    {
      name: 'Pion',
      id: '90x60',
      imagesCount: 1,
      views: [1, 2, 3, 4],
      hasThickness: true,
    },
    {
      name: 'Poziom',
      id: '60x90',
      imagesCount: 1,
      views: [1, 2, 3, 4],
      hasThickness: true,
    },
    {
      name: '3 piony',
      id: '3x90x60',
      imagesCount: 3,
      views: [1, 2, 3, 4],
      hasThickness: false,
    },
    {
      id: '2x60x40+90x60',
      name: '2 poziomy i pion',
      imagesCount: 3,
      views: [1, 2],
      hasThickness: false,
    },
  ];
  uploadInProgress = 0;
  model = this.typedFormBuilder.typedGroup<{
    url: Array<string>;
    size: string;
    view: string;
    scene: string;
    crop: Array<number>;
    spread: boolean;
    color: string;
    thickness: string;
  }>({
    url: new UntypedFormArray([]),
    size: [],
    view: ['1'],
    crop: [[0, 0, 0, 0]],
    scene: [],
    spread: [false],
    thickness: ['1'],
    color: ['rgba(0,0,0,1)'],
  });
  loadingInProgress$ = new BehaviorSubject(false);
  updateCrop$ = new Subject();
  currentViews$ = this.model.controls.size.valueChanges.pipe(
    map((scene) => this.scenes.find((s) => s.id === scene)?.views)
  );
  currentThickness$ = this.model.controls.size.valueChanges.pipe(
    map((scene) => this.scenes.find((s) => s.id === scene)?.hasThickness)
  );
  imageRatio$ = this.model.valueChanges.pipe(
    pluck('url'),
    switchMap((url) =>
      from(
        new Promise((resolve) => {
          const i = new Image();
          i.addEventListener('load', () => {
            resolve(i.width / i.height);
          });
          // i.src = url;
        })
      )
    )
  );
  buffer;
  @ViewChild('preview', { static: true }) preview: ElementRef<HTMLImageElement>;
  cache: Record<string, string> = {};
  containerRatio$ = this.model.valueChanges.pipe(
    pluck('size'),
    map((size) => {
      const s = size.split('x').map((v) => Number(v));
      return s[1] / s[0];
    })
  );

  get textColor() {
    const { color } = this.model.value;

    return `rgba(${color.substring(4, color.length - 1)})`;
  }

  set textColor(color) {
    console.log(color);
    if (color.substr(0, 4) === 'rgba') {
      const colors = color
        .substring(4)
        .substring(1, color.substring(4).length - 1)
        .split(',');
      console.log(colors);
      color = `rgba(${colors[0]},${colors[1]},${colors[2]},${colors[3]})`;
    }
    this.model.patchValue({
      color,
    });
  }

  get urls() {
    return this.model.controls.url as UntypedFormArray;
  }

  constructor(
    private typedFormBuilder: TypedFormBuilder,
    private httpClient: HttpClient,
    private domSanitizer: DomSanitizer,
    public organizationService: OrganizationService
  ) {
    super();
  }

  ngOnInit(): void {
    let updateInProgress = false;
    this.subscribeWhileAlive(
      this.updateCrop$.pipe(
        debounceTime(1000),
        tap((crop) => this.model.patchValue({ crop }))
      ),
      this.model.controls.size.valueChanges.pipe(
        filter(() => !updateInProgress),
        tap((sceneId) => {
          console.log(sceneId);
          const currentScene = this.scenes.find((s) => s.id === sceneId);
          updateInProgress = true;
          while (this.urls.length !== 0) {
            this.urls.removeAt(0);
          }
          for (let i = 0; i < currentScene.imagesCount; i++) {
            this.urls.push(new UntypedFormControl(''));
          }
          updateInProgress = false;
        })
      ),
      this.model.valueChanges.pipe(
        filter(() => !updateInProgress),
        debounceTime(100),
        tap(() => {}),
        filter(() => !!this.model.value.size && this.uploadInProgress === 0),
        switchMap((c) => {
          const currentScene = this.scenes.find((s) => s.id === c.size);

          const data = {
            ...c,
            images: this.urls.value.map((v) => ({
              image: v
                ? `https://obrazki.zalamo.com/public-image?fileName=${v}&organizationId=${this.organizationService.lastOrganizationId}&size=1920`
                : null,
              crop: [],
              mirroring: false,
            })),
            organizationId: this.organizationService.lastOrganizationId,
          };
          if (!currentScene.hasThickness) {
            data.thickness = '1';
          }

          if (this.model.value.spread) {
            if (this.model.value.size === '3x90x60') {
              data.images[0].crop = [10, 66, 10, 0];
              data.images[1] = {
                ...data.images[0],
                crop: [10, 33, 10, 36],
              };
              data.images[2] = {
                ...data.images[0],
                crop: [10, 0, 10, 69],
              };
            }
            if (this.model.value.size === '2x60x40+90x60') {
              data.images[0].crop = [5, 50, 55, 0];
              data.images[1] = {
                ...data.images[0],
                crop: [55, 50, 5, 0],
              };
              data.images[2] = {
                ...data.images[0],
                crop: [5, 0, 5, 60],
              };
            }
          }
          const index = JSON.stringify(data);
          if (this.cache[index]) {
            this.preview.nativeElement.src = this.cache[index];
            return of(null);
          }
          this.loadingInProgress$.next(true);
          return this.httpClient
            .post(`${environment.renderUrl}/../render-canvas`, data, {
              responseType: 'blob',
            })
            .pipe(
              tap((buffer) => {
                this.preview.nativeElement.src = URL.createObjectURL(buffer);
                this.buffer = this.domSanitizer.bypassSecurityTrustUrl(
                  this.preview.nativeElement.src
                );

                this.cache[index] = this.preview.nativeElement.src;
                this.loadingInProgress$.next(false);
              })
            );
        })
      ),
      timer(200).pipe(
        tap(() => {
          this.model.patchValue({
            size: '90x60',
          });
        })
      )
    );
  }
}
